import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  describePAApiError,
  getSpecialistFeed,
  saveHoroscopeProfile,
  searchHoroscopeCities,
  type HoroscopeBirthTimeAccuracy,
  type HoroscopeCity,
  type PASpecialistFeed,
} from '../../../lib/api/team.js';
import { hapticSuccess, hapticTap } from '../../../lib/native.js';
import { useSkillBack } from '../../../lib/skillBack.js';
import { PageShell } from '../../stage/PageShell.js';
import { Skeleton } from '../../ui/Skeleton.js';
import './horoscopo.css';

interface NatalPoint {
  id: string;
  name: string;
  glyph: string;
  longitude: number;
  signId: string;
  sign: string;
  signGlyph: string;
  degree: number;
  minute: number;
  house?: number | undefined;
  approximate?: boolean | undefined;
}

interface NatalChart {
  timeAccuracy: HoroscopeBirthTimeAccuracy;
  houseSystem: 'whole-sign' | null;
  points: NatalPoint[];
  trio: { sun: NatalPoint; moon: NatalPoint; ascendant: NatalPoint | null };
}

interface Transit {
  id: string;
  transitPoint: string;
  transitGlyph: string;
  natalPoint: string;
  aspect: string;
  exactness: number;
  headline: string;
  guidance: string;
  tone: string;
}

interface TodayData {
  generatedAt?: string | undefined;
  moonPhase?: { angle: number; label: string; glyph: string };
  transits?: Transit[] | undefined;
}

function readChart(feed?: PASpecialistFeed): NatalChart | null {
  const chart = feed?.config?.chart;
  return chart && typeof chart === 'object' ? (chart as unknown as NatalChart) : null;
}

// Força o símbolo astrológico a sair como texto, não como emoji colorido.
function textGlyph(glyph: string): string {
  return `${glyph}︎`;
}

function degreeLabel(point: NatalPoint | null): string {
  if (!point) return 'Complete quando souber';
  return `${point.degree}°${String(point.minute).padStart(2, '0')}′${point.approximate ? ', aproximado' : ''}`;
}

function transitMeta(transit: Transit): string {
  return `${transit.transitPoint} · ${transit.aspect} · orbe de ${transit.exactness.toFixed(1)}°`;
}

function todayLabel(): string {
  return new Intl.DateTimeFormat('pt-BR', {
    day: 'numeric',
    month: 'long',
    timeZone: 'America/Sao_Paulo',
  }).format(new Date());
}

function Welcome({ onStart, onBack }: { onStart: () => void; onBack: () => void }) {
  return (
    <PageShell
      kicker="Horóscopo"
      title="Seu céu, explicado para você"
      lead="Eu monto o mapa do seu nascimento e trago os movimentos que merecem atenção hoje."
      onBack={onBack}
    >
      <section className="pgs-section">
        <div className="pgs-row">
          <span className="pgs-ic hv-glyph">{textGlyph('☉')}</span>
          <span className="pgs-rowt">
            <b>Sol, Lua e Ascendente</b>
            <small>O seu trio principal, com grau e casa</small>
          </span>
        </div>
        <div className="pgs-row">
          <span className="pgs-ic hv-glyph">{textGlyph('☾')}</span>
          <span className="pgs-rowt">
            <b>Uma leitura por dia</b>
            <small>Os trânsitos que tocam o seu mapa, em poucas linhas</small>
          </span>
        </div>
      </section>
      <div className="pgs-actions hv-actions">
        <button
          className="pgs-btn solid"
          onClick={() => {
            void hapticTap();
            onStart();
          }}
        >
          Montar meu mapa
        </button>
      </div>
      <p className="hv-fine">
        Leva cerca de 1 minuto. Conteúdo de autoconhecimento e entretenimento.
      </p>
    </PageShell>
  );
}

const SETUP_COPY: Record<number, { title: string; lead: string }> = {
  1: {
    title: 'Quando você nasceu?',
    lead: 'A data posiciona o Sol, a Lua e os planetas no seu mapa.',
  },
  2: {
    title: 'Você sabe a hora?',
    lead: 'A hora revela o Ascendente e as casas. Uma diferença de minutos pode mudar o resultado.',
  },
  3: {
    title: 'Em que cidade?',
    lead: 'O lugar define o horizonte daquele momento e o fuso horário da época.',
  },
  4: {
    title: 'Como o seu céu entra na rotina?',
    lead: 'Você escolhe o ritmo. Nada de avisos em excesso.',
  },
};

const ACCURACY: ReadonlyArray<readonly [HoroscopeBirthTimeAccuracy, string, string]> = [
  ['exact', 'Sei a hora', 'Está no registro'],
  ['approximate', 'É aproximada', 'Pode variar um pouco'],
  ['unknown', 'Não sei', 'Sem Ascendente e casas'],
];

function Setup({ onSaved, onBack }: { onSaved: () => Promise<void>; onBack: () => void }) {
  const [step, setStep] = useState(1);
  const [birthDate, setBirthDate] = useState('');
  const [accuracy, setAccuracy] = useState<HoroscopeBirthTimeAccuracy>('exact');
  const [birthTime, setBirthTime] = useState('12:00');
  const [cityQuery, setCityQuery] = useState('');
  const [cities, setCities] = useState<HoroscopeCity[]>([]);
  const [place, setPlace] = useState<HoroscopeCity | null>(null);
  const [searching, setSearching] = useState(false);
  const [morningBrief, setMorningBrief] = useState(true);
  const [transitAlerts, setTransitAlerts] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const requestRef = useRef(0);

  useEffect(() => {
    if (step !== 3 || place || cityQuery.trim().length < 2) {
      setCities([]);
      return;
    }
    const requestId = ++requestRef.current;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const result = await searchHoroscopeCities(cityQuery.trim());
        if (requestId === requestRef.current) setCities(result.cities);
      } catch {
        if (requestId === requestRef.current) setCities([]);
      } finally {
        if (requestId === requestRef.current) setSearching(false);
      }
    }, 280);
    return () => clearTimeout(timer);
  }, [cityQuery, place, step]);

  const canContinue =
    step === 1
      ? Boolean(birthDate)
      : step === 2
        ? accuracy === 'unknown' || Boolean(birthTime)
        : step === 3
          ? Boolean(place)
          : true;

  async function next() {
    setError('');
    if (!canContinue) return;
    void hapticTap();
    if (step < 4) setStep((value) => value + 1);
    else {
      if (!place) return;
      setSaving(true);
      try {
        await saveHoroscopeProfile(
          {
            birthDate,
            birthTime: accuracy === 'unknown' ? null : birthTime,
            birthTimeAccuracy: accuracy,
            place,
          },
          { morningBrief, transitAlerts },
        );
        void hapticSuccess();
        await onSaved();
      } catch (err) {
        setError(
          describePAApiError(
            err,
            'Não consegui calcular o mapa. Confira os dados e tente novamente.',
          ),
        );
      } finally {
        setSaving(false);
      }
    }
  }

  const copy = SETUP_COPY[step] ?? SETUP_COPY[1]!;

  return (
    <PageShell kicker="Horóscopo" title={copy.title} lead={copy.lead} onBack={onBack}>
      <div className="pgs-bar hv-progress">
        <span>Conhecendo o seu céu</span>
        <b>{step} de 4</b>
        <i>
          <em style={{ width: `${step * 25}%` }} />
        </i>
      </div>

      <div key={step} className="hv-step sk-cascade">
        {step === 1 && (
          <>
            <label className="hv-field">
              <span>Data de nascimento</span>
              <input
                type="date"
                value={birthDate}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setBirthDate(e.target.value)}
              />
            </label>
            <div className="pgs-note">
              <i>i</i>
              <span>
                <b>Protegido de verdade</b>
                <small>
                  Seus dados ficam criptografados e não aparecem em registros do sistema.
                </small>
              </span>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <section className="pgs-section">
              {ACCURACY.map(([id, label, help]) => (
                <button
                  key={id}
                  className={`pgs-row hv-choice${accuracy === id ? ' on' : ''}`}
                  aria-pressed={accuracy === id}
                  onClick={() => setAccuracy(id)}
                >
                  <span className="pgs-ic">{accuracy === id ? '✓' : ''}</span>
                  <span className="pgs-rowt">
                    <b>{label}</b>
                    <small>{help}</small>
                  </span>
                </button>
              ))}
            </section>
            {accuracy !== 'unknown' && (
              <label className="hv-field">
                <span>{accuracy === 'exact' ? 'Hora de nascimento' : 'Melhor estimativa'}</span>
                <input
                  type="time"
                  value={birthTime}
                  onChange={(e) => setBirthTime(e.target.value)}
                />
              </label>
            )}
            {accuracy === 'unknown' && (
              <div className="pgs-note">
                <i>i</i>
                <span>
                  <b>Sem a hora, ainda dá</b>
                  <small>
                    Calculo o Sol e os planetas com segurança. A Lua fica marcada como aproximada.
                  </small>
                </span>
              </div>
            )}
          </>
        )}

        {step === 3 && (
          <>
            <label className="hv-field">
              <span>Cidade de nascimento</span>
              <input
                value={place?.label ?? cityQuery}
                onChange={(e) => {
                  setPlace(null);
                  setCityQuery(e.target.value);
                }}
                placeholder="Ex.: Campinas, São Paulo"
                autoComplete="off"
              />
            </label>
            {searching && <p className="hv-hint">Procurando cidades…</p>}
            {!searching && cities.length > 0 && (
              <div className="pgs-section">
                {cities.map((city) => (
                  <button
                    key={city.id}
                    className="pgs-row hv-choice"
                    onClick={() => {
                      setPlace(city);
                      setCityQuery(city.label);
                      setCities([]);
                    }}
                  >
                    <span className="pgs-ic hv-glyph">⌖</span>
                    <span className="pgs-rowt">
                      <b>{city.city}</b>
                      <small>{[city.region, city.country].filter(Boolean).join(', ')}</small>
                    </span>
                  </button>
                ))}
              </div>
            )}
            {!searching && !place && cityQuery.length >= 2 && cities.length === 0 && (
              <p className="hv-hint">Continue digitando ou confira o nome da cidade.</p>
            )}
            {place && (
              <div className="pgs-row">
                <span className="pgs-ic">✓</span>
                <span className="pgs-rowt">
                  <b>{place.label}</b>
                  <small>Fuso {place.timezone}</small>
                </span>
              </div>
            )}
            <p className="hv-fine">Base de cidades: SimpleMaps, licença CC BY 4.0.</p>
          </>
        )}

        {step === 4 && (
          <>
            <section className="pgs-section">
              <button
                className={`pgs-row hv-toggle${morningBrief ? ' on' : ''}`}
                role="switch"
                aria-checked={morningBrief}
                onClick={() => setMorningBrief(!morningBrief)}
              >
                <span className="pgs-ic hv-glyph">{textGlyph('☀')}</span>
                <span className="pgs-rowt">
                  <b>No meu Bom Dia</b>
                  <small>Uma linha quando houver algo relevante</small>
                </span>
                <span className="hv-switch" aria-hidden="true" />
              </button>
              <button
                className={`pgs-row hv-toggle${transitAlerts ? ' on' : ''}`}
                role="switch"
                aria-checked={transitAlerts}
                onClick={() => setTransitAlerts(!transitAlerts)}
              >
                <span className="pgs-ic hv-glyph">✦</span>
                <span className="pgs-rowt">
                  <b>Trânsito marcante</b>
                  <small>Aviso raro, só para movimentos importantes</small>
                </span>
                <span className="hv-switch" aria-hidden="true" />
              </button>
            </section>
            <section className="pgs-section">
              <h2 className="pgs-h2">Seu mapa</h2>
              <div className="pgs-row">
                <span className="pgs-ic hv-glyph">{textGlyph('☉')}</span>
                <span className="pgs-rowt">
                  <b>
                    {birthDate.split('-').reverse().join('/')},{' '}
                    {accuracy === 'unknown' ? 'hora desconhecida' : birthTime}
                  </b>
                  <small>{place?.label}</small>
                </span>
              </div>
            </section>
          </>
        )}
      </div>

      {error && (
        <p className="hv-error" role="alert">
          {error}
        </p>
      )}
      <div className="pgs-actions hv-actions">
        {step > 1 && (
          <button
            className="pgs-btn"
            onClick={() => setStep((value) => value - 1)}
            disabled={saving}
          >
            Anterior
          </button>
        )}
        <button className="pgs-btn solid" disabled={!canContinue || saving} onClick={next}>
          {saving ? 'Calculando o seu céu…' : step === 4 ? 'Revelar meu mapa' : 'Continuar'}
        </button>
      </div>
    </PageShell>
  );
}

function Dashboard({
  feed,
  onReconfigure,
  onBack,
}: {
  feed: PASpecialistFeed;
  onReconfigure: () => void;
  onBack: () => void;
}) {
  const chart = readChart(feed)!;
  const details = (feed.items.find((item) => item.kind === 'horoscope_daily')?.details ??
    {}) as TodayData;
  const transits = Array.isArray(details.transits) ? details.transits : [];
  const [main, ...others] = transits;
  const placeLabel =
    typeof feed.config.placeLabel === 'string' ? feed.config.placeLabel : undefined;
  const trio = [
    { label: 'Seu Sol', point: chart.trio.sun, copy: 'Sua direção essencial' },
    { label: 'Sua Lua', point: chart.trio.moon, copy: 'Seu mundo emocional' },
    {
      label: 'Ascendente',
      point: chart.trio.ascendant,
      copy: chart.trio.ascendant ? 'Como você chega' : 'Hora necessária',
    },
  ];
  const planets = chart.points.filter((point) => point.id !== 'ascendant');

  return (
    <PageShell
      kicker="Horóscopo"
      title={`${chart.trio.sun.sign}, ${todayLabel()}`}
      lead={placeLabel ? `Mapa de quem nasceu em ${placeLabel}.` : undefined}
      onBack={onBack}
    >
      {details.moonPhase && (
        <div className="pgs-figure">
          <b>{details.moonPhase.label}</b>
          <small>
            <span className="hv-inline-glyph">{textGlyph(details.moonPhase.glyph)}</span> Fase da
            Lua hoje
          </small>
        </div>
      )}

      {main ? (
        <section className="pgs-section">
          <h2 className="pgs-h2">Leitura do dia</h2>
          <article className="hv-reading">
            <b>{main.headline}</b>
            <p>{main.guidance}</p>
            <small>{transitMeta(main)}</small>
          </article>
        </section>
      ) : (
        <p className="pgs-empty">Estou separando os movimentos de hoje.</p>
      )}

      {others.length > 0 && (
        <section className="pgs-section">
          <h2 className="pgs-h2">
            Outros movimentos <small>{others.length}</small>
          </h2>
          {others.map((transit) => (
            <div className="pgs-row hv-transit" key={transit.id}>
              <span className="pgs-ic hv-glyph">{textGlyph(transit.transitGlyph)}</span>
              <span className="pgs-rowt">
                <b>{transit.headline}</b>
                <small>{transitMeta(transit)}</small>
                <span className="hv-guidance">{transit.guidance}</span>
              </span>
            </div>
          ))}
        </section>
      )}

      <section className="pgs-section">
        <h2 className="pgs-h2">Seu trio principal</h2>
        {trio.map(({ label, point, copy }) => (
          <div key={label} className={`pgs-row${point ? '' : ' hv-off'}`}>
            <span className="pgs-ic hv-glyph">{textGlyph(point?.signGlyph ?? '◌')}</span>
            <span className="pgs-rowt">
              <b>{label}</b>
              <small>
                {copy}, {degreeLabel(point).toLowerCase()}
              </small>
            </span>
            <span className="pgs-val">{point ? point.sign : 'Sem hora'}</span>
          </div>
        ))}
      </section>

      <section className="pgs-section">
        <h2 className="pgs-h2">
          O céu do seu nascimento <small>{planets.length}</small>
        </h2>
        {planets.map((point) => (
          <div className="pgs-row" key={point.id}>
            <span className="pgs-ic hv-glyph">{textGlyph(point.glyph)}</span>
            <span className="pgs-rowt">
              <b>{point.name}</b>
              <small>
                {point.degree}°{String(point.minute).padStart(2, '0')}′
                {point.house ? `, casa ${point.house}` : ''}
                {point.approximate ? ', aproximado' : ''}
              </small>
            </span>
            <span className="pgs-val">{point.sign}</span>
          </div>
        ))}
      </section>

      <div className="pgs-note">
        <i>?</i>
        <span>
          <b>Pergunte quando quiser</b>
          <small>“Qual é meu ascendente?” ou “Como está meu céu hoje?”</small>
        </span>
      </div>

      <p className="hv-fine">
        Astrologia é uma linguagem simbólica de autoconhecimento e entretenimento. Decisões
        importantes continuam sendo suas.
      </p>
      <div className="pgs-actions">
        <button className="pgs-btn" onClick={onReconfigure}>
          Refazer meus dados de nascimento
        </button>
      </div>
    </PageShell>
  );
}

export function HoroscopoView() {
  const back = useSkillBack();
  const qc = useQueryClient();
  const feedQ = useQuery({
    queryKey: ['pa-specialist-feed', 'horoscopo'],
    queryFn: () => getSpecialistFeed('horoscopo'),
    refetchInterval: (query) => (query.state.data?.generating ? 1800 : false),
  });
  const [started, setStarted] = useState(false);
  const [reconfigure, setReconfigure] = useState(false);
  const configured = feedQ.data?.config?.configured === true && Boolean(readChart(feedQ.data));

  return useMemo(() => {
    if (feedQ.isLoading)
      return (
        <PageShell kicker="Horóscopo" title="Seu céu" onBack={back}>
          <Skeleton variant="rows" label="Abrindo o seu céu" />
        </PageShell>
      );
    if (feedQ.isError || !feedQ.data)
      return (
        <PageShell
          kicker="Horóscopo"
          title="Não consegui abrir o seu céu agora"
          lead="Pode ter sido uma falha passageira. Tento de novo quando você pedir."
          onBack={back}
        >
          <div className="pgs-actions">
            <button className="pgs-btn solid" onClick={() => feedQ.refetch()}>
              Tentar novamente
            </button>
          </div>
        </PageShell>
      );
    if (configured && !reconfigure)
      return (
        <Dashboard feed={feedQ.data} onReconfigure={() => setReconfigure(true)} onBack={back} />
      );
    if (!started && !reconfigure) return <Welcome onStart={() => setStarted(true)} onBack={back} />;
    return (
      <Setup
        onBack={back}
        onSaved={async () => {
          setReconfigure(false);
          setStarted(false);
          await qc.invalidateQueries({ queryKey: ['pa-specialist-feed', 'horoscopo'] });
          await feedQ.refetch();
        }}
      />
    );
    // `back` fica de fora: só navega para a Início e não muda o que a tela mostra.
  }, [configured, feedQ.data, feedQ.isError, feedQ.isLoading, qc, reconfigure, started]);
}
